import { z } from "zod";
import type { SubscriptionStatus } from "../enums";

// SaaS billing (WP10): plans, subscription, usage versus plan limits, subscription payments through the platform's
// Flouci account (decision D26) and manual activation by a platform admin. Prices are integer minor units.

export const PLAN_LIMIT_KEYS = ["aiTokensMonthly", "clients", "members", "conversationsMonthly", "ordersMonthly", "sites", "adAccounts"] as const;
export type PlanLimitKey = (typeof PLAN_LIMIT_KEYS)[number];

export const PLAN_LIMIT_LABELS: Record<PlanLimitKey, string> = {
  aiTokensMonthly: "Tokens IA par mois",
  clients: "Clients (boutiques)",
  members: "Membres",
  conversationsMonthly: "Conversations par mois",
  ordersMonthly: "Commandes par mois",
  sites: "Sites et landing pages",
  adAccounts: "Comptes publicitaires",
};

export const PLAN_FEATURE_KEYS = ["autoReply", "mediaBuyer", "siteBuilder", "playbooks", "reports", "customDomains", "byokAllowed"] as const;
export type PlanFeatureKey = (typeof PLAN_FEATURE_KEYS)[number];

export const PLAN_FEATURE_LABELS: Record<PlanFeatureKey, string> = {
  autoReply: "Réponses automatiques IA",
  mediaBuyer: "Media Buyer (Meta, TikTok, Google)",
  siteBuilder: "Constructeur de site",
  playbooks: "Playbooks et relances",
  reports: "Rapports planifiés",
  customDomains: "Domaines personnalisés",
  byokAllowed: "Votre propre clé IA",
};

export const PLAN_INTERVALS = ["month", "year"] as const;
export type PlanInterval = (typeof PLAN_INTERVALS)[number];

export const PLAN_INTERVAL_LABELS: Record<PlanInterval, string> = { month: "par mois", year: "par an" };

export const BILLING_PROVIDERS = ["flouci"] as const;
export type BillingProvider = (typeof BILLING_PROVIDERS)[number];

export const BILLING_PROVIDER_LABELS: Record<BillingProvider | "manual", string> = {
  flouci: "Flouci",
  manual: "Activation manuelle",
};

/** Durations offered at checkout for monthly plans (yearly plans are bought by multiples of 12 months). */
export const BILLING_MONTH_OPTIONS = [1, 3, 6, 12] as const;

export const BILLING_PAYMENT_STATUSES = ["created", "pending", "paid", "failed", "expired"] as const;
export type BillingPaymentStatus = (typeof BILLING_PAYMENT_STATUSES)[number];

export const BILLING_PAYMENT_STATUS_LABELS: Record<BillingPaymentStatus, string> = {
  created: "Créé",
  pending: "En attente",
  paid: "Payé",
  failed: "Échoué",
  expired: "Expiré",
};

export const SUBSCRIPTION_EVENT_LABELS: Record<string, string> = {
  trial_started: "Essai démarré",
  trial_extended: "Essai prolongé",
  trial_expired: "Essai terminé",
  activated: "Abonnement activé",
  renewed: "Abonnement prolongé",
  plan_changed: "Formule modifiée",
  past_due: "Paiement en retard",
  expired: "Abonnement expiré",
  canceled: "Abonnement résilié",
  cancel_scheduled: "Résiliation programmée",
  cancel_reverted: "Résiliation annulée",
};

export const billingCheckoutSchema = z.object({
  planId: z.string().regex(/^[a-f0-9]{24}$/i, "Formule invalide"),
  months: z.number().int("Durée invalide").min(1, "Durée invalide").max(36, "36 mois maximum"),
  provider: z.enum(BILLING_PROVIDERS),
});
export type BillingCheckoutInput = z.infer<typeof billingCheckoutSchema>;

export const billingPaymentListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type BillingPaymentListQuery = z.infer<typeof billingPaymentListQuery>;

export interface BillingPlanDto {
  id: string;
  code: string;
  name: string;
  description: string;
  priceMinor: number;
  currency: string;
  interval: PlanInterval;
  trialDays: number;
  limits: Record<PlanLimitKey, number>;
  features: Record<PlanFeatureKey, boolean>;
  public: boolean;
  active: boolean;
  sortOrder: number;
  highlighted: boolean;
}

export interface BillingUsageItemDto {
  key: PlanLimitKey;
  used: number;
  limit: number;
}

export interface BillingSubscriptionDto {
  plan: BillingPlanDto;
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  provider: "manual" | "flouci" | "none";
  /** Whole days left before the trial or the paid period ends (null when not applicable). */
  daysLeft: number | null;
  /** End of the 7 day grace period while a payment is late. */
  graceEndsAt: string | null;
  usable: boolean;
  usage: BillingUsageItemDto[];
  /** Platform admin override of the monthly AI token quota (null = plan value). */
  aiTokenOverride: number | null;
  period: string;
  history: Array<{ event: string; at: string; data: Record<string, unknown> | null }>;
  checkout: Record<BillingProvider, boolean>;
}

export interface BillingPaymentDto {
  id: string;
  planId: string;
  planName: string;
  amountMinor: number;
  currency: string;
  months: number;
  provider: "manual" | "flouci";
  status: BillingPaymentStatus;
  /** Payment link while the payment is still payable. */
  paymentUrl: string | null;
  reference: string | null;
  note: string;
  paidAt: string | null;
  periodEnd: string | null;
  createdAt: string;
}

export interface BillingCheckoutResultDto {
  paymentId: string;
  paymentUrl: string;
  amountMinor: number;
  currency: string;
  provider: BillingProvider;
  expiresAt: string | null;
}

/** Amount of a checkout: monthly plans are billed per month, yearly plans per 12 months. */
export function planCheckoutAmount(plan: { priceMinor: number; interval: PlanInterval }, months: number): number {
  return plan.interval === "year" ? Math.round(plan.priceMinor * (months / 12)) : plan.priceMinor * months;
}

/** Durations allowed at checkout for a plan. */
export function planMonthOptions(interval: PlanInterval): number[] {
  return interval === "year" ? [12, 24] : [...BILLING_MONTH_OPTIONS];
}
